import React, { useEffect, useState } from "react";
import { useTranslation } from "../context/LanguageContext";
import { Helmet } from "react-helmet-async";
import UpdatingMessage from "../components/ui/UpdatingMessage";

const News = () => {
  const { locale, translate } = useTranslation();
  const [pdfConfig, setPdfConfig] = useState({ enabled: false, image: "/test.webp" });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const loadConfig = async () => {
      try {
        const res = await fetch(`/pdfs.json`, { cache: "no-store" });
        if (!res.ok) throw new Error("Failed to load pdfs.json");
        const data = await res.json();
        if (!cancelled) {
          setPdfConfig({
            enabled: Boolean(data.enabled),
            image: typeof data.image === "string" && data.image ? data.image : "/test.webp",
          });
        }
      } catch (_err) {
        if (!cancelled) {
          setPdfConfig({ enabled: false, image: "/test.webp" });
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    loadConfig();
    return () => {
      cancelled = true;
    };
  }, []);

  if (locale === "kr") {
    return (
      <>
        <Helmet>
          <title>{translate("meta.title_news")}</title>
          <meta name="description" content={translate("meta.description")} />
        </Helmet>
        <UpdatingMessage />
      </>
    );
  }

  if (isLoading) {
    return null;
  }

  if (!pdfConfig.enabled) {
    return (
      <>
        <Helmet>
          <title>{translate("meta.title_news")}</title>
          <meta name="description" content={translate("meta.description")} />
        </Helmet>
        <UpdatingMessage />
      </>
    );
  }

  return (
    <div style={{ margin: 0, padding: 0, background: "#ffffff", minHeight: "100vh" }}>
      <Helmet>
        <title>{translate("meta.title_news")}</title>
        <meta name="description" content={translate("meta.description")} />
      </Helmet>
      <div style={{ display: "flex", justifyContent: "center", flexDirection: "column", alignItems: "center" }}>
        <div style={{ maxWidth: "100%", background: "#ffffff", width: "100%", display: "flex", justifyContent: "center" }}>
          <ProgressiveImage
            placeholderSrc="/test.bk.webp"
            fullSrc={pdfConfig.image}
            alt="Document image"
            width={2160}
            height={15599}
          />
        </div>
      </div>
    </div>
  );
};

function ProgressiveImage({ placeholderSrc, fullSrc, width, height, alt }) {
  const [src, setSrc] = useState(placeholderSrc);
  const [isFull, setIsFull] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const img = new Image();
    const onReady = () => {
      if (cancelled) return;
      setSrc(fullSrc);
      setIsFull(true);
    };
    const onError = () => {
      if (cancelled) return;
      // Fallback: keep placeholder but remove blur so it isn't permanently blurry
      setIsFull(true);
    };
    img.onload = onReady;
    img.onerror = onError;
    img.src = fullSrc;
    if (img.decode) {
      img.decode().then(onReady).catch(() => {/* noop, onload will fire */});
    }
    // Safety timeout to remove blur even if onload never fires (older browsers)
    const safety = setTimeout(() => {
      if (!cancelled) setIsFull(true);
    }, 3000);
    return () => {
      cancelled = true;
      clearTimeout(safety);
    };
  }, [fullSrc]);

  return (
    <img
      src={src}
      alt={alt}
      width={width}
      height={height}
      style={{
        width: "100%",
        height: "auto",
        display: "block",
        background: "#fff",
        aspectRatio: `${width} / ${height}`,
        filter: isFull ? "none" : "blur(6px)",
        transition: "filter 300ms ease",
      }}
      loading="lazy"
      decoding="async"
      fetchpriority="low"
    />
  );
}

export default News;
